import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, message } from "antd";
import Table from "../../../components/Table/Table";
import ConfidencePill from "../../../components/ConfidencePill";
import { normalizeConfidence } from "../../MailIntake/utils";
import {
  ListViewWrapper,
  ListToolbar,
  ListTableWrapper,
  StatusPill,
  SfWriteStatusPill,
  DetectionFlagPill,
  PaymentPill,
  MutedText,
  MetaText,
  ResultName,
  LinkBtn,
} from "../FeeNoticeQueueStyled";
import {
  CLICKABLE_STATUSES,
  STATUS_LABELS,
  SF_WRITE_STATUS_LABELS,
  describePaymentStatus,
  formatDate,
} from "../utils";
import {
  claimFeeNotice,
  fetchFeeNoticeQueue,
  setFeeNoticeView,
  setQueuePage,
  setQueueScope,
  QUEUE_PAGE_SIZE,
} from "../../../slice/FeeNoticeSlice";
import useAuth from "../../../hooks/useAuth";

const SCOPE_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "confirmed", label: "Confirmed" },
  { value: "dismissed", label: "Dismissed" },
];

const EmptyCell = () => <MutedText>—</MutedText>;

export const columns = [
  {
    title: "Letter",
    dataIndex: "document_name",
    key: "document_name",
    ellipsis: true,
    width: "26%",
    render: (name) => <ResultName style={{ fontSize: 13 }}>{name || "—"}</ResultName>,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 220,
    render: (s, record) => (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <StatusPill status={s}>{STATUS_LABELS[s] || s}</StatusPill>
        {record.classification?.disagreement && (
          <DetectionFlagPill>Check detection</DetectionFlagPill>
        )}
      </div>
    ),
  },
  {
    title: "Matter",
    key: "matter",
    width: "22%",
    // Always name the client. This column used to print the literal
    // words "Suggested match" plus a percentage, which told a reviewer
    // nothing about who the letter was for and forced them to open
    // every row to find out (Matthew, 2026-08-19). `match_display_name`
    // is the backend's single answer to "who is this letter for":
    // the committed matter, else the sorter's assignment, else the
    // matcher's suggestion — all resolved to a name.
    render: (_, record) => {
      const name = record.match_display_name || record.matter_display_name;
      if (!name) {
        return <MutedText>Unmatched</MutedText>;
      }
      const unconfirmed = !record.matter_id;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ResultName style={{ fontSize: 13 }}>{name}</ResultName>
          {unconfirmed && record.auto_match?.confidence != null && (
            <ConfidencePill
              value={normalizeConfidence(record.auto_match.confidence)}
            />
          )}
          {unconfirmed && record.auto_match?.source === "sorter" && (
            <MetaText>from mail sorting</MetaText>
          )}
        </div>
      );
    },
  },
  {
    // Who handled (or is handling) the letter. Confirms clear the
    // claim, so terminal rows show the confirmer/dismisser instead of
    // a blank cell (Matthew, 2026-07-27).
    title: "Reviewer",
    key: "reviewer",
    width: 160,
    render: (_, record) => {
      const name =
        record.confirmed_by?.name ||
        record.dismissed_by?.name ||
        record.claimed_by?.name;
      return name ? <MetaText>{name}</MetaText> : <EmptyCell />;
    },
  },
  {
    title: "Received",
    dataIndex: "created_at",
    key: "created_at",
    width: 170,
    render: formatDate,
  },
  {
    // Payload paid state, maintained by the payment webhook. Blank
    // until a payment event lands — most letters never show anything
    // here, so an empty cell beats a wall of "Awaiting payment".
    title: "Payment",
    dataIndex: "payment_status",
    key: "payment_status",
    width: 170,
    render: (ps) => {
      const view = describePaymentStatus(ps);
      if (!view) return <EmptyCell />;
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          title={view.detail || undefined}
        >
          <PaymentPill tone={view.tone}>{view.label}</PaymentPill>
        </div>
      );
    },
  },
  {
    title: "Salesforce",
    dataIndex: "sf_write_status",
    key: "sf_write_status",
    width: 170,
    render: (s, record) =>
      record.status === "confirmed" ? (
        <SfWriteStatusPill status={s}>
          {SF_WRITE_STATUS_LABELS[s] || s}
        </SfWriteStatusPill>
      ) : (
        <EmptyCell />
      ),
  },
];

const ListView = () => {
  const dispatch = useDispatch();
  const { queue, queueLoading, queueScope, queuePage, view } = useSelector(
    (state) => state.feeNotices
  );
  // The team dashboard's backend endpoint is role-gated; hiding the
  // button for everyone else just avoids a guaranteed 403.
  const { isAdmin } = useAuth();

  // Server-side pagination: scope or page changing means a different
  // slice of the queue, so refetch. (Scope changes also reset the page
  // to 1 in the slice.) `view` is a dependency because this component
  // now stays mounted while the reviewer works a letter (the views are
  // CSS-toggled for document-engine warmth): returning to the list must
  // refresh it — the letter just worked has to drop off — and before,
  // that refresh came from this component remounting.
  useEffect(() => {
    if (view !== "list") return;
    dispatch(fetchFeeNoticeQueue());
  }, [dispatch, queueScope, queuePage, view]);

  const handleScopeChange = (value) => {
    dispatch(setQueueScope(value));
  };

  const handlePageChange = (page) => {
    dispatch(setQueuePage(page));
  };

  // Claiming mirrors the mail sorter's row-click flow: attempt the claim,
  // let the backend's specific 409 message (claimed by a teammate,
  // already processed) surface via toast, and only switch views on
  // success (handled by the slice's claimFeeNotice.fulfilled reducer).
  const handleRowClick = async (record) => {
    if (!CLICKABLE_STATUSES.has(record.status)) {
      const reason =
        record.status === "extracting"
          ? "The AI is still reading this letter — check back in a minute."
          : record.status === "confirmed"
          ? "This letter has already been confirmed."
          : "This letter was dismissed.";
      message.info(reason);
      return;
    }
    const result = await dispatch(claimFeeNotice(record.id));
    if (!claimFeeNotice.fulfilled.match(result)) {
      message.error(result.payload || "Could not open this letter");
    }
  };

  const results = queue?.items || [];
  const shown = results.length;
  // Real scope size from the API; the items array is one server page.
  const total = queue?.total ?? queue?.count ?? 0;
  const rangeStart = shown ? (queuePage - 1) * QUEUE_PAGE_SIZE + 1 : 0;
  const rangeEnd = (queuePage - 1) * QUEUE_PAGE_SIZE + shown;

  return (
    <ListViewWrapper>
      <ListToolbar>
        <Select
          value={queueScope}
          onChange={handleScopeChange}
          options={SCOPE_OPTIONS}
          style={{ minWidth: 150, fontFamily: "Manrope" }}
          popupMatchSelectWidth={false}
        />
        <MetaText>
          {total > shown
            ? `Showing ${rangeStart}–${rangeEnd} of ${total} letters`
            : `${total} letter${total === 1 ? "" : "s"}`}
        </MetaText>
        {isAdmin && (
          <LinkBtn
            style={{ marginLeft: "auto" }}
            onClick={() => dispatch(setFeeNoticeView("activity"))}
          >
            Team activity
          </LinkBtn>
        )}
      </ListToolbar>

      <ListTableWrapper>
        <Table
          columns={columns}
          data={results}
          loading={queueLoading}
          emptyMessage="No fee notice letters in this view"
          // The shared Table defaults to pagination={false}; the spread
          // in that component lets this override it. Server-side: the
          // rows above are already one page, so antd only renders the
          // pager and reports page flips.
          pagination={{
            current: queuePage,
            pageSize: QUEUE_PAGE_SIZE,
            total,
            onChange: handlePageChange,
            showSizeChanger: false,
            position: ["bottomCenter"],
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: {
              cursor: CLICKABLE_STATUSES.has(record.status)
                ? "pointer"
                : "default",
            },
          })}
        />
      </ListTableWrapper>
    </ListViewWrapper>
  );
};

export default ListView;
